(() => {
  const routes = window.terrorTalesRoutes || {};
  const drawers = Array.from(document.querySelectorAll('[data-drawer]'));
  const scrim = document.querySelector('[data-drawer-scrim]');
  const liveRegion = document.querySelector('[data-cart-live]');
  let activeDrawer = null;
  let lastTrigger = null;

  const headerShell = document.querySelector('[data-header-shell]');
  if (headerShell?.dataset.stickyHeader === 'true') {
    let previousScrollY = window.scrollY;
    let headerFrame = 0;

    const updateStickyHeader = () => {
      const currentScrollY = Math.max(0, window.scrollY);
      const triggerPoint = Math.max(56, headerShell.scrollHeight);
      const delta = currentScrollY - previousScrollY;

      if (currentScrollY <= triggerPoint) {
        headerShell.classList.remove('is-sticky-active', 'is-sticky-visible');
      } else {
        headerShell.classList.add('is-sticky-active');
        if (delta < -3) headerShell.classList.add('is-sticky-visible');
        if (delta > 3) headerShell.classList.remove('is-sticky-visible');
      }

      if (Math.abs(delta) >= 3 || currentScrollY <= triggerPoint) previousScrollY = currentScrollY;
      headerFrame = 0;
    };

    window.addEventListener('scroll', () => {
      if (!headerFrame) headerFrame = window.requestAnimationFrame(updateStickyHeader);
    }, { passive: true });
    updateStickyHeader();
  }

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const money = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const announce = (message) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 30);
  };

  const closeDrawers = (restoreFocus = true) => {
    const trigger = lastTrigger;
    const hadActiveDrawer = Boolean(activeDrawer);
    drawers.forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('[data-drawer-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (scrim) scrim.hidden = true;
    document.body.style.overflow = '';
    document.body.classList.remove('tt-drawer-active');
    activeDrawer = null;
    lastTrigger = null;
    if (restoreFocus && hadActiveDrawer && trigger?.isConnected) trigger.focus();
  };

  const openDrawer = (name, trigger) => {
    closeDrawers(false);
    const drawer = document.querySelector(`[data-drawer="${name}"]`);
    if (!drawer) return;
    lastTrigger = trigger || document.querySelector(`[data-drawer-open="${name}"]`);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (lastTrigger) lastTrigger.setAttribute('aria-expanded', 'true');
    if (scrim) scrim.hidden = false;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('tt-drawer-active');
    activeDrawer = drawer;
    window.setTimeout(() => drawer.querySelector('input, button, a, summary')?.focus(), 30);
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.dataset.drawerOpen, opener);
      return;
    }
    if (event.target.closest('[data-drawer-close]')) closeDrawers();
  });

  document.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') {
      closeDrawers();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(activeDrawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'))
      .filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const shippingMarkup = (container, cart) => {
    if (container.dataset.freeShippingEnabled !== 'true') return '';
    const threshold = Number(container.dataset.freeShippingThreshold || 0);
    if (!threshold) return '';
    const remaining = threshold - cart.total_price;
    const progress = Math.max(0, Math.min(100, (cart.total_price / threshold) * 100));
    const progressText = container.dataset.freeShippingMessage || 'You are [amount] away from free shipping.';
    const successText = container.dataset.freeShippingSuccess || 'You unlocked free shipping.';
    const message = remaining > 0 ? progressText.replace('[amount]', money(remaining, cart.currency)) : successText;
    return `<div class="tt-free-shipping"><p>${escapeHtml(message)}</p><div class="tt-free-shipping-track"><span style="width:${progress}%"></span></div></div>`;
  };

  const readGiftConfig = () => {
    const node = document.querySelector('[data-free-gift-config]');
    if (!node) return null;
    try {
      const config = JSON.parse(node.textContent);
      return config.variants && config.variants.length ? config : null;
    } catch (_) { return null; }
  };

  const giftMarkup = (cart) => {
    const giftConfig = readGiftConfig();
    if (!giftConfig) return '';
    const ids = giftConfig.variants.map((v) => v.id);
    if (cart.items.some((item) => ids.includes(item.variant_id))) return '';
    const qualifying = cart.items
      .filter((item) => !ids.includes(item.variant_id))
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const remaining = giftConfig.threshold - qualifying;
    if (remaining > 0) {
      if (!giftConfig.progress) return '';
      return `<p class="tt-drawer-gift-progress">${escapeHtml(giftConfig.progress.replace('[amount]', money(remaining, cart.currency)))}</p>`;
    }
    const rows = giftConfig.variants.map((v) => `
      <div class="tt-cart-drawer-item tt-drawer-gift-item">
        <a href="${escapeHtml(v.url)}">${v.image ? `<img src="${escapeHtml(v.image)}" alt="" width="88" height="110" loading="lazy">` : ''}</a>
        <div class="tt-cart-item-copy">
          <a href="${escapeHtml(v.url)}">${escapeHtml(v.title)}</a>
          ${v.variant ? `<small>${escapeHtml(v.variant)}</small>` : ''}
          <span class="tt-drawer-gift-price">${v.price ? `<s>${money(v.price, cart.currency)}</s> ` : ''}<strong>FREE</strong></span>
          <button type="button" class="tt-drawer-gift-add" data-drawer-gift-add data-variant-id="${v.id}">Add free ${escapeHtml(giftConfig.gift)}</button>
        </div>
      </div>`).join('');
    return `
      <div class="tt-drawer-gift" data-drawer-gift>
        <p class="tt-drawer-gift-heading">${escapeHtml(giftConfig.heading)}</p>
        ${rows}
      </div>`;
  };

  const renderCart = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = String(cart.item_count);
    });
    const container = document.querySelector('[data-cart-drawer-content]');
    if (!container) return;
    if (!cart.items.length) {
      const message = escapeHtml(container.dataset.emptyMessage || 'Your bag is empty.');
      const button = escapeHtml(container.dataset.emptyButton || 'Continue shopping');
      container.innerHTML = `${shippingMarkup(container, cart)}<div class="tt-empty-cart"><p>${message}</p><a href="${routes.root || '/'}collections/all">${button}</a></div>`;
      return;
    }

    const showImages = container.dataset.showImages !== 'false';
    const items = cart.items.map((item) => {
      const image = showImages && item.image
        ? `<a href="${escapeHtml(item.url)}"><img src="${escapeHtml(item.image)}" alt="" width="88" height="110"></a>`
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<small>${escapeHtml(item.variant_title)}</small>`
        : '';
      return `
        <div class="tt-cart-drawer-item${showImages ? '' : ' tt-cart-drawer-item--no-image'}">
          ${image}
          <div class="tt-cart-item-copy">
            <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${variant}
            <span>${money(item.final_line_price, cart.currency)}</span>
            <div class="tt-cart-quantity">
              <button type="button" data-cart-quantity data-key="${escapeHtml(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-quantity data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
          </div>
          <button class="tt-cart-remove" type="button" data-cart-quantity data-key="${escapeHtml(item.key)}" data-quantity="0" aria-label="Remove ${escapeHtml(item.product_title)}">×</button>
        </div>`;
    }).join('');

    const subtotal = escapeHtml(container.dataset.subtotalLabel || 'Subtotal');
    const checkout = escapeHtml(container.dataset.checkoutLabel || 'Checkout');
    const checkoutNote = escapeHtml(container.dataset.checkoutNote || '');
    const viewCart = escapeHtml(container.dataset.viewCartLabel || 'View bag');
    container.innerHTML = `
      ${shippingMarkup(container, cart)}
      <div class="tt-cart-drawer-items">${items}</div>
      ${giftMarkup(cart)}
      <div class="tt-cart-drawer-summary">
        <p><span>${subtotal}</span><strong>${money(cart.total_price, cart.currency)}</strong></p>
        <form action="${routes.cart || '/cart'}" method="post"><button class="tt-button tt-button--dark" type="submit" name="checkout">${checkout}</button></form>
        ${checkoutNote ? `<p class="tt-cart-checkout-note">${checkoutNote}</p>` : ''}
        <a class="tt-cart-view-link" href="${routes.cart || '/cart'}">${viewCart}</a>
      </div>`;
  };

  const getCart = async () => {
    const response = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  };

  const setQuickAddOpen = (card, open) => {
    if (!card) return;
    const toggle = card.querySelector('[data-quick-add-toggle]');
    const overlay = card.querySelector('[data-quick-add-overlay]');
    card.classList.toggle('is-quick-add-open', open);
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
    if (overlay) {
      overlay.setAttribute('aria-hidden', String(!open));
      overlay.querySelectorAll('button').forEach((button) => {
        if (open && !button.disabled) button.removeAttribute('tabindex');
        else button.setAttribute('tabindex', '-1');
      });
    }
  };

  const closeQuickAdds = (except) => {
    document.querySelectorAll('.tt-product-card.is-quick-add-open').forEach((card) => {
      if (card !== except) setQuickAddOpen(card, false);
    });
  };

  document.addEventListener('click', async (event) => {
    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (!quantityButton) return;
    quantityButton.disabled = true;
    try {
      const response = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: quantityButton.dataset.key, quantity: Number(quantityButton.dataset.quantity) })
      });
      if (!response.ok) throw new Error('Unable to update cart');
      renderCart(await response.json());
      announce('Cart updated.');
    } catch (_) {
      quantityButton.disabled = false;
      announce('Something went wrong. Please try again.');
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.js-quick-add, .js-product-form');
    if (!form) return;
    event.preventDefault();
    const button = event.submitter || form.querySelector('[type="submit"]');
    const originalText = button?.dataset.addText || button?.textContent || 'Add to bag';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }
    try {
      const formData = new FormData(form);
      if (button?.dataset.variantId) formData.set('id', button.dataset.variantId);
      const response = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!response.ok) throw new Error('Unable to add item');
      renderCart(await getCart());
      if (button) button.textContent = 'Added';
      announce('Added to bag.');
      closeQuickAdds();
      openDrawer('cart');
    } catch (_) {
      if (button) button.textContent = 'Try again';
      announce('Something went wrong. Please try again.');
    } finally {
      window.setTimeout(() => {
        if (!button) return;
        button.disabled = false;
        button.textContent = originalText;
      }, 1200);
    }
  });

  document.addEventListener('change', (event) => {
    const stickyVariant = event.target.closest('[data-sticky-product-variant]');
    if (!stickyVariant) return;
    const variantButton = Array.from(document.querySelectorAll('[data-product-variant]'))
      .find((button) => button.dataset.id === stickyVariant.value);
    variantButton?.click();
  });

  document.addEventListener('click', (event) => {
    const detailsOpen = event.target.closest('[data-product-details-open]');
    if (detailsOpen) {
      const desktop = window.matchMedia('(min-width: 900px)').matches;
      const details = desktop
        ? document.querySelector('.tt-product-details-desktop')
        : document.querySelector('.tt-product-details-mobile, .tt-product-details-static');
      if (details?.tagName === 'DETAILS') details.open = true;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      details?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      return;
    }

    const stickySubmit = event.target.closest('[data-sticky-product-submit]');
    if (stickySubmit) {
      const form = document.querySelector('.js-product-form');
      const submit = form?.querySelector('[data-product-submit]');
      if (form && submit && !submit.disabled) {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submit);
        else submit.click();
      }
      return;
    }

    const gridViewButton = event.target.closest('[data-grid-view]');
    if (gridViewButton) {
      const grid = document.querySelector('[data-collection-grid]');
      if (!grid) return;
      const singleColumn = gridViewButton.dataset.gridView === '1';
      grid.classList.toggle('is-single-column', singleColumn);
      document.querySelectorAll('[data-grid-view]').forEach((button) => {
        const selected = button === gridViewButton;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      return;
    }

    const quickAddToggle = event.target.closest('[data-quick-add-toggle]');
    if (quickAddToggle) {
      event.preventDefault();
      const card = quickAddToggle.closest('.tt-product-card');
      const shouldOpen = !card.classList.contains('is-quick-add-open');
      closeQuickAdds(card);
      setQuickAddOpen(card, shouldOpen);
      if (shouldOpen) card.querySelector('[data-quick-add-overlay] button:not(:disabled)')?.focus();
      return;
    }

    if (!event.target.closest('[data-quick-add-overlay]')) closeQuickAdds();

    const variantButton = event.target.closest('[data-product-variant]');
    if (variantButton) {
      const form = variantButton.closest('.js-product-form');
      form.querySelectorAll('[data-product-variant]').forEach((button) => {
        button.classList.toggle('is-selected', button === variantButton);
        button.setAttribute('aria-pressed', String(button === variantButton));
      });
      form.querySelector('[data-product-id]').value = variantButton.dataset.id;
      const price = document.querySelector('[data-product-price]');
      if (price) price.textContent = variantButton.dataset.price;
      const stickyPrice = document.querySelector('[data-sticky-product-price]');
      if (stickyPrice) stickyPrice.textContent = variantButton.dataset.price;
      const stickyVariant = document.querySelector('[data-sticky-product-variant]');
      if (stickyVariant) stickyVariant.value = variantButton.dataset.id;
      const shipping = document.querySelector('[data-product-shipping]');
      if (shipping) {
        const threshold = Number(shipping.dataset.threshold || 0);
        const priceCents = Number(variantButton.dataset.priceCents || 0);
        shipping.textContent = priceCents >= threshold
          ? shipping.dataset.qualified
          : shipping.dataset.belowThreshold.replace('[amount]', money(Math.max(0, threshold - priceCents)));
      }
      const compare = document.querySelector('[data-product-compare]');
      if (compare) {
        compare.textContent = variantButton.dataset.compare || '';
        compare.hidden = !variantButton.dataset.compare;
      }
      return;
    }

    const quantityButton = event.target.closest('[data-product-quantity-change]');
    if (quantityButton) {
      const form = quantityButton.closest('.js-product-form');
      const input = form.querySelector('[data-product-quantity-input]');
      const display = form.querySelector('[data-product-quantity]');
      const next = Math.max(1, Number(input.value) + Number(quantityButton.dataset.productQuantityChange));
      input.value = String(next);
      if (display) display.textContent = String(next);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeQuickAdds();
  });

  const initRichTextTables = (root = document) => {
    root.querySelectorAll('.tt-product-description table').forEach((table) => {
      const parent = table.parentElement;
      let wrapper = parent;

      if (parent?.classList.contains('tt-table-scroll')) {
        wrapper = parent;
      } else if (parent?.classList.contains('table-wrapper')) {
        parent.classList.add('tt-table-scroll');
        wrapper = parent;
      } else {
        wrapper = document.createElement('div');
        wrapper.className = 'tt-table-scroll';
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', 'Size chart');
        wrapper.setAttribute('tabindex', '0');
        parent?.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      }

      const columnCount = Array.from(table.rows).reduce((maximum, row) => {
        const rowColumns = Array.from(row.cells).reduce((total, cell) => total + (cell.colSpan || 1), 0);
        return Math.max(maximum, rowColumns);
      }, 0);

      wrapper.dataset.tableColumns = String(columnCount);
      wrapper.classList.toggle('tt-table-scroll--wide', columnCount >= 8);
      wrapper.classList.toggle('tt-table-scroll--extra-wide', columnCount >= 10);
      table.classList.toggle('tt-size-table--wide', columnCount >= 8);
      table.classList.toggle('tt-size-table--extra-wide', columnCount >= 10);
    });
  };

  initRichTextTables();
  document.addEventListener('shopify:section:load', (event) => initRichTextTables(event.target));

  document.querySelectorAll('[data-sticky-product-atc]').forEach((sticky) => {
    const page = sticky.closest('.tt-product-page');
    const mainButton = page?.querySelector('[data-product-submit]');
    const stickyButton = sticky.querySelector('[data-sticky-product-submit]');
    if (!page || !mainButton || !stickyButton) return;
    const mobile = window.matchMedia('(max-width: 899px)');
    let stickyFrame = 0;
    const updateStickyProductButton = () => {
      const mainBounds = mainButton.getBoundingClientRect();
      const pageBounds = page.getBoundingClientRect();
      const visible = mobile.matches && mainBounds.bottom < 0 && pageBounds.bottom > sticky.offsetHeight + 20;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      stickyButton.disabled = mainButton.disabled;
      stickyFrame = 0;
    };
    const requestStickyUpdate = () => {
      if (!stickyFrame) stickyFrame = window.requestAnimationFrame(updateStickyProductButton);
    };
    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    window.addEventListener('resize', requestStickyUpdate, { passive: true });
    if (mobile.addEventListener) mobile.addEventListener('change', requestStickyUpdate);
    else mobile.addListener(requestStickyUpdate);
    updateStickyProductButton();
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const shell = gallery.closest('.tt-product-gallery-shell');
    const items = Array.from(gallery.querySelectorAll('.tt-product-gallery-item'));
    const previous = shell?.querySelector('[data-gallery-previous]');
    const next = shell?.querySelector('[data-gallery-next]');
    if (!shell || items.length < 2 || !previous || !next) return;

    const mobileGallery = window.matchMedia('(max-width: 899px)');
    const makeLoopClone = (item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('tt-product-gallery-item--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
      return clone;
    };

    gallery.prepend(makeLoopClone(items[items.length - 1]));
    gallery.append(makeLoopClone(items[0]));

    let frame = 0;
    let settleTimer = 0;
    let currentIndex = 0;
    const pageWidth = () => Math.max(gallery.clientWidth, 1);
    const pageFromScroll = () => Math.round(gallery.scrollLeft / pageWidth());
    const jumpToPage = (page) => {
      gallery.scrollLeft = page * pageWidth();
    };
    const settleLoop = () => {
      if (!mobileGallery.matches) return;
      const page = pageFromScroll();
      if (page <= 0) {
        currentIndex = items.length - 1;
        jumpToPage(items.length);
      } else if (page >= items.length + 1) {
        currentIndex = 0;
        jumpToPage(1);
      } else {
        currentIndex = page - 1;
      }
    };
    const updateGalleryNav = () => {
      previous.disabled = !mobileGallery.matches && currentIndex === 0;
      next.disabled = !mobileGallery.matches && currentIndex === items.length - 1;
    };
    const moveGallery = (direction) => {
      if (!mobileGallery.matches) return;
      let page = pageFromScroll();
      if (page <= 0) page = items.length;
      if (page >= items.length + 1) page = 1;
      gallery.scrollTo({ left: (page + direction) * pageWidth(), behavior: 'smooth' });
    };
    const resetGalleryPosition = () => {
      if (mobileGallery.matches) jumpToPage(currentIndex + 1);
      else gallery.scrollLeft = 0;
      updateGalleryNav();
    };

    previous.addEventListener('click', () => moveGallery(-1));
    next.addEventListener('click', () => moveGallery(1));
    gallery.addEventListener('scroll', () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (mobileGallery.matches) {
          const page = pageFromScroll();
          if (page > 0 && page < items.length + 1) currentIndex = page - 1;
        }
        updateGalleryNav();
      });
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settleLoop, 100);
    }, { passive: true });
    gallery.addEventListener('scrollend', settleLoop, { passive: true });
    window.addEventListener('resize', resetGalleryPosition, { passive: true });
    if (mobileGallery.addEventListener) mobileGallery.addEventListener('change', resetGalleryPosition);
    else mobileGallery.addListener(resetGalleryPosition);
    window.requestAnimationFrame(resetGalleryPosition);
  });

  const initProductRail = (container) => {
    if (!container || container.dataset.railReady === 'true') return;
    const grid = container.querySelector('.tt-product-grid');
    const scrollbar = container.querySelector('[data-product-rail-scrollbar]');
    const thumb = scrollbar?.querySelector('[data-product-rail-thumb]');
    if (!grid || !scrollbar || !thumb) return;
    container.dataset.railReady = 'true';
    let railFrame = 0;

    const updateRail = () => {
      const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
      scrollbar.hidden = maxScroll <= 1;
      if (maxScroll > 1) {
        const trackWidth = scrollbar.clientWidth;
        const thumbWidth = Math.max(46, trackWidth * (grid.clientWidth / grid.scrollWidth));
        const travel = Math.max(0, trackWidth - thumbWidth);
        const offset = travel * (grid.scrollLeft / maxScroll);
        thumb.style.width = `${thumbWidth}px`;
        thumb.style.transform = `translateX(${offset}px)`;
      }
      railFrame = 0;
    };

    grid.addEventListener('scroll', () => {
      if (!railFrame) railFrame = window.requestAnimationFrame(updateRail);
    }, { passive: true });
    window.addEventListener('resize', updateRail, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(updateRail).observe(grid);
    updateRail();
  };

  document.querySelectorAll('[data-product-recommendations]').forEach(async (container) => {
    if (container.innerHTML.trim()) {
      initProductRail(container);
      return;
    }
    if (!container.dataset.url) return;
    try {
      const response = await fetch(container.dataset.url);
      if (!response.ok) return;
      const html = document.createElement('div');
      html.innerHTML = await response.text();
      const replacement = html.querySelector('[data-product-recommendations]');
      if (replacement?.innerHTML.trim()) {
        container.innerHTML = replacement.innerHTML;
        initProductRail(container);
      }
    } catch (_) {
      // Recommendations are optional and fail silently.
    }
  });
  document.addEventListener('click', async (event) => {
    const add = event.target.closest('[data-drawer-gift-add]');
    if (!add) return;
    add.disabled = true;
    add.textContent = 'Adding…';
    try {
      const response = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(add.dataset.variantId), quantity: 1, properties: { _free_gift: (readGiftConfig() || {}).gift || 'gift' } }] })
      });
      if (!response.ok) throw new Error('Unable to add gift');
      renderCart(await getCart());
      announce('Free gift added.');
    } catch (_) {
      add.disabled = false;
      add.textContent = 'Try again';
      announce('Something went wrong. Please try again.');
    }
  });

  const refreshGift = () => {
    if (readGiftConfig()) getCart().then(renderCart).catch(() => {});
  };
  refreshGift();
  document.addEventListener('shopify:section:load', refreshGift);
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-drawer-open="cart"]')) refreshGift();
  });

  document.addEventListener('free-gift:added', (event) => {
    if (event.detail) renderCart(event.detail);
    openDrawer('cart');
  });
})();
